import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

/**
 * Shared page shell: header wordmark + a footer with the legal links.
 *
 * Type follows the app's two-tier split (docs/DESIGN-SYSTEM.md §9.4): the
 * wordmark and page title are Inter Tight, everything anyone actually reads is
 * Inter. Nav labels are lowercase, matching the app's UI voice; prose and legal
 * headings keep normal sentence case, because a privacy policy is a document,
 * not chrome.
 */
export function PageChrome({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="border-b-[2.5px] border-ink bg-surface">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between px-6 py-4">
          <Link
            to="/"
            className="font-display text-xl font-bold tracking-tight text-ink"
          >
            Fazly
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 px-6 py-12">
        {title ? (
          <h1 className="font-display text-[34px] leading-[1.3] font-bold tracking-tight text-ink">
            {title}
          </h1>
        ) : null}
        {children}
      </main>

      <footer className="border-t-[2.5px] border-ink bg-surface">
        <div className="mx-auto flex w-full max-w-4xl flex-wrap gap-5 px-6 py-8 text-sm text-ink2">
          {/* Same reason as the landing page's footer: every page links to
              the tools hub, so no tool is more than two clicks from anywhere.
              A page reachable only from the sitemap is one Google finds late
              and treats as unimportant. */}
          <Link
            to="/tools"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            free tools
          </Link>
          <Link
            to="/about"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            about
          </Link>
          <Link
            to="/privacy"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            privacy
          </Link>
          <Link
            to="/terms"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            terms
          </Link>
          <Link
            to="/support"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            support
          </Link>
          <Link
            to="/delete"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            delete account
          </Link>
        </div>
      </footer>
    </div>
  );
}
